// hueRing — 色相リング (ドーナツ) の純粋な角度⇄Hue 変換 + ジオメトリ計算
// (consult 2026-06-21、カラーピッカーを CSP/Java 互換の「色相リング + 内接正方形SV」に).
//
// 背景: α フィードバックで「色相環(H/S)+明度スライダだと黒/暗色が取りにくい。CSP/Java 版と
// 同じ『色相リング + 内接した正方形(横=S・縦=V)』が欲しい」。本ファイルは DOM / React に
// 触らない pure function だけを置き、vitest (node 環境) で角度⇄Hue 変換 / 内接正方形サイズを
// テスト可能にする (既存 colorWheel.ts / colorChannelBars.ts の pure helper 流儀)。
//
// 角度の定義 (CSS conic-gradient と一致させる):
//   - リングは CSS `conic-gradient(from 0deg, ...)` で描く = **真上 (12 時) が 0deg、時計回り**。
//   - よって Hue H (0..360) は「真上から時計回りに H 度」の位置に対応する。
//   - pointer のヒット位置 (中心からのベクトル) → 画面座標は「右が +x, 下が +y」。
//     真上方向は (0, -1)。時計回り角度 θ = atan2(dx, -dy) (rad)。これを度に直して
//     [0, 360) に正規化したものが Hue。

/** 円周率まわりの定数。 */
const TWO_PI = Math.PI * 2;
const RAD_TO_DEG = 360 / TWO_PI;
const DEG_TO_RAD = TWO_PI / 360;

/** 値を [0, 360) に正規化 (負やラップアラウンドを吸収)。 */
export const normalizeHue = (deg: number): number => ((deg % 360) + 360) % 360;

/**
 * リング上の pointer 位置 (中心からの相対座標 dx, dy = 画面座標系で右が +x・下が +y) を
 * Hue (0..360) に変換する。真上 (12 時) を 0、時計回りに増加 (= conic-gradient と一致)。
 *
 * dx=dy=0 (中心ちょうど) は angle 0 (= 真上) を返す。
 */
export const hueFromPointer = (dx: number, dy: number): number => {
  // 中心ちょうど (0,0) は角度が定まらない (atan2(0,-0)=π になる) ので 0 を返す。
  if (dx === 0 && dy === 0) return 0;
  // 真上 (0,-1) を基準に時計回り。atan2(dx, -dy) が「真上から時計回りの角度 (rad)」。
  const rad = Math.atan2(dx, -dy);
  return normalizeHue(rad * RAD_TO_DEG);
};

/** 単位円上の Hue 位置 (中心基準の単位ベクトル)。真上が H=0、時計回り。 */
export const unitVectorForHue = (hue: number): { readonly x: number; readonly y: number } => {
  const rad = normalizeHue(hue) * DEG_TO_RAD;
  // 真上 (0,-1) を時計回りに rad 回転: x = sin, y = -cos。
  return { x: Math.sin(rad), y: -Math.cos(rad) };
};

/**
 * リングのジオメトリ。outer = リング外径(px)、thickness = リング太さ(px)。
 * 内接正方形の一辺 = リング内径 × cos45°(≒0.7071) で、リング中央に絶対配置する。
 */
export interface RingGeometry {
  /** リング外径 (px)。 */
  readonly outer: number;
  /** リング太さ (px)。 */
  readonly thickness: number;
  /** リング内径 (px) = outer - 2 × thickness。 */
  readonly inner: number;
  /** 内接正方形の一辺 (px) = inner × cos45°。 */
  readonly square: number;
  /** 内接正方形の左上オフセット (px、リング外枠の左上基準) = (outer - square) / 2。 */
  readonly squareInset: number;
  /** Hue ノブの中心半径 (px) = リング中央 = outer/2 - thickness/2。 */
  readonly knobRadius: number;
}

/** cos45° = 1/√2。 */
const COS45 = Math.SQRT1_2;

/** outer/thickness から内接正方形サイズ等を導出する。 */
export const ringGeometry = (outer: number, thickness: number): RingGeometry => {
  const inner = outer - thickness * 2;
  const square = inner * COS45;
  return {
    outer,
    thickness,
    inner,
    square,
    squareInset: (outer - square) / 2,
    knobRadius: outer / 2 - thickness / 2,
  };
};

/**
 * pointer がリング部分 (= 中央の内接円より外) に当たっているか。
 *
 * `dx, dy` は `getBoundingClientRect` 基準 (= 画面上の見た目の px)、`renderedOuter` はその rect の
 * 幅。祖先に CSS `zoom` (サイドバー表示サイズ、iPad 既定 72%) が掛かると、rect と clientX/Y は
 * 縮んだ見た目の px になるが `g.inner` は縮む前の CSS px のまま。両者を直接比べると 72% では
 * リング全域が「内側」判定になり色相が選べなかった (2026-10-13 iPad 実機 fb)。内径を外径に
 * 対する比率にして見た目の外径に掛け、同じ座標系で比べる (`HueRing` の mask を % にしたのと同じ考え方)。
 */
export const isPointerOnRing = (
  dx: number,
  dy: number,
  renderedOuter: number,
  g: RingGeometry,
): boolean => Math.hypot(dx, dy) >= (renderedOuter / 2) * (g.inner / g.outer);
